<template>
  <div class="search-form">
    <div class="search-form__container mx-auto">
      <div class="search-form__head">
        <span class="search-form__close-icon" @click="hideSearchForm" />
        <span class="search-form__title"> 絞り込み </span>
      </div>
      <div class="search-form__body">
        <search-section>
          <template #title> 予算 </template>
          <template #body>
            <SearchInputGroup
              class="search-form__budget"
              :lower-budget="lowerBudget"
              :higher-budget="higherBudget"
              :errors="errors"
              @inputted-lower-budget="onInputLowerBudget"
              @inputted-higher-budget="onInputHigherBudget"
            />
          </template>
        </search-section>
        <search-section>
          <template #title> カテゴリ </template>
          <template #body>
            <SearchSelectMulti
              class="search-form__category"
              prefix-for-label="search-form__category"
              :elements="categories"
              :default-values="selectedCategoryIds"
              @changed="onSelectCategory"
            />
          </template>
        </search-section>
        <search-section>
          <template #title> シーン </template>
          <template #body>
            <SearchSelectMulti
              class="search-form__scene"
              prefix-for-label="search-form__scene"
              :elements="scenes"
              :default-values="selectedSceneIds"
              @changed="onSelectScene"
            />
          </template>
        </search-section>
        <search-section>
          <template #title> 受け取り方 </template>
          <template #body>
            <SearchSelectMulti
              class="search-form__receiving-kind"
              prefix-for-label="search-form__receiving-kind"
              :elements="receivingKinds"
              :default-values="selectedReceivingKindIds"
              @changed="onSelectReceivingKind"
            />
          </template>
        </search-section>
        <div class="search-form__counter-section">
          <div v-if="isLoadingItemCount" class="loading-section">
            <SmallLoading />
          </div>
          <div v-else class="search-form__counter counter">
            <span class="hit_count"> {{ hittedItemCount }}件ヒット </span>
          </div>
        </div>
      </div>
    </div>
    <div class="search-form__footer">
      <button
        class="btn btn--primary search-form__submit"
        :disabled="!submitable"
        @click="onSubmitSearchForm"
      >
        絞り込む
      </button>
      <button
        class="btn btn--cancel search-form__close"
        @click="hideSearchForm"
      >
        閉じる
      </button>
    </div>
  </div>
</template>
<script>
import SearchSection from './SearchSection.vue'
import SearchInputGroup from './SearchInputGroup.vue'
import SearchSelectMulti from './SearchSelectMulti.vue'
import SmallLoading from '../../atoms/loadings/SmallLoading.vue'
import SearchItemPaginationQuery from '../../../../../graphqls/public/queries/search_item_pagination'
import * as Sentry from '@sentry/browser'

const MIN_BUDGET = 0
const MAX_BUDGET = 10000000

export default {
  components: {
    SearchSection,
    SearchInputGroup,
    SearchSelectMulti,
    SmallLoading
  },
  props: {
    keyword: {
      type: String,
      required: true
    },
    lowerBudget: {
      required: true,
      validator: prop => typeof prop === 'number' || prop === null
    },
    higherBudget: {
      required: true,
      validator: prop => typeof prop === 'number' || prop === null
    },
    categories: {
      type: Array,
      required: true
    },
    selectedCategoryIds: {
      type: Array,
      required: true
    },
    scenes: {
      type: Array,
      required: true
    },
    selectedSceneIds: {
      type: Array,
      required: true
    },
    receivingKinds: {
      type: Array,
      required: true
    },
    selectedReceivingKindIds: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      inputtedLowerBudget: null,
      inputtedHigherBudget: null,
      inputtedSelectedCategoryIds: [],
      inputtedSelectedSceneIds: [],
      inputtedSelectedReceivingKindIds: [],
      hittedItemCount: 0,
      isLoadingItemCount: false,
      errors: []
    }
  },
  computed: {
    submitable() {
      return this.errors.length === 0
    }
  },
  mounted() {
    this.inputtedLowerBudget = this.lowerBudget
    this.inputtedHigherBudget = this.higherBudget
    this.inputtedSelectedCategoryIds = this.selectedCategoryIds
    this.inputtedSelectedSceneIds = this.selectedSceneIds
    this.inputtedSelectedReceivingKindIds = this.selectedReceivingKindIds
    if (this.validateBudget()) {
      this.getItemCount()
    }
  },
  methods: {
    hideSearchForm() {
      this.$emit('click-hide')
    },
    validateBudget() {
      const errors = []
      if (this.inputtedLowerBudget != null) {
        if (this.inputtedLowerBudget < MIN_BUDGET) {
          errors.push({
            attribute: 'lower_budget',
            message: `金額は${MIN_BUDGET}円以上でご入力ください`
          })
        }
        if (this.inputtedLowerBudget > MAX_BUDGET) {
          errors.push({
            attribute: 'lower_budget',
            message: `金額は${(
              MAX_BUDGET / 10000
            ).toLocaleString()}万円以下でご入力ください`
          })
        }
      }

      if (this.inputtedHigherBudget != null) {
        if (this.inputtedHigherBudget < MIN_BUDGET) {
          errors.push({
            attribute: 'higher_budget',
            message: `金額は${MIN_BUDGET}円以上でご入力ください`
          })
        }
        if (this.inputtedHigherBudget > MAX_BUDGET) {
          errors.push({
            attribute: 'higher_budget',
            message: `金額は${(
              MAX_BUDGET / 10000
            ).toLocaleString()}万円以下でご入力ください`
          })
        }
      }

      this.errors = errors
      return this.errors.length === 0
    },
    onInputLowerBudget(value) {
      this.inputtedLowerBudget = value
      this.isLoadingItemCount = true

      if (this.validateBudget()) {
        this.getItemCount()
      }
    },
    onInputHigherBudget(value) {
      this.inputtedHigherBudget = value
      this.isLoadingItemCount = true

      if (this.validateBudget()) {
        this.getItemCount()
      }
    },
    onSelectCategory(selectedIds) {
      this.inputtedSelectedCategoryIds = selectedIds
      this.getItemCount()
    },
    onSelectScene(selectedIds) {
      this.inputtedSelectedSceneIds = selectedIds
      this.getItemCount()
    },
    onSelectReceivingKind(selectedIds) {
      this.inputtedSelectedReceivingKindIds = selectedIds
      this.getItemCount()
    },
    onSubmitSearchForm() {
      this.$emit(
        'submit',
        this.inputtedLowerBudget,
        this.inputtedHigherBudget,
        this.inputtedSelectedCategoryIds,
        this.inputtedSelectedSceneIds,
        this.inputtedSelectedReceivingKindIds
      )
    },
    async getItemCount() {
      this.isLoadingItemCount = true
      try {
        const response = await this.$apollo.provider.clients.publicClient.query(
          {
            query: SearchItemPaginationQuery,
            variables: {
              keyword: this.keyword,
              pageNumber: 1, // ページネーションの情報のみ欲しいため、最低限の条件に
              itemPerPage: 1, // ページネーションの情報のみ欲しいため、最低限の条件に
              itemReceivingKindIds: this.inputtedSelectedReceivingKindIds,
              sortKey: null,
              sceneIds: this.inputtedSelectedSceneIds,
              categoryIds: this.inputtedSelectedCategoryIds,
              stringLowerBudget:
                this.inputtedLowerBudget != null
                  ? this.inputtedLowerBudget.toString()
                  : this.inputtedLowerBudget,
              stringHigherBudget:
                this.inputtedHigherBudget != null
                  ? this.inputtedHigherBudget.toString()
                  : this.inputtedHigherBudget
            }
          }
        )

        if (response.data) {
          const { totalCount } = response.data.searchItems.pagination
          this.hittedItemCount = totalCount

          setTimeout(() => {
            this.isLoadingItemCount = false
          }, 300)
        }
      } catch (error) {
        Sentry.captureException(error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.search-form {
  position: fixed;
  top: 0;
  left: 0;
  background-color: $white;
  width: 100%;
  height: 100%;
  // NOTE: header の z-index が 100 で指定されているため 101 を指定
  z-index: 101;
  overflow-y: scroll;
  border-radius: 0;

  &__container {
    max-width: 960px;
  }

  &__head {
    margin: auto;
    padding-right: 32px;
    font-weight: bold;
    display: flex;
    position: sticky;
    top: 0;
    background-color: white;
    padding: 16px;
    align-items: center;
    justify-content: center;
    z-index: 1;
  }

  &__close-icon {
    @include bg-settings(
      '../../../../../images/icons/close.svg',
      $white,
      32px,
      32px
    );
    padding-top: 32px;
    padding-left: 32px;
  }

  &__title {
    font-size: 1.8rem;
    font-weight: bold;
    color: $black;
    line-height: 1.6;
    margin: auto;
    padding-right: 32px;
  }

  &__body {
    padding-top: 24px;
  }

  &__budget {
    padding: 24px 16px;
  }

  &__category {
    margin-bottom: 40px;
  }

  &__scene {
    margin-bottom: 40px;
  }

  &__counter-section {
    padding-top: 41px;
  }

  &__counter {
    text-align: center;
    margin-bottom: 16px;
  }

  &__footer {
    display: grid;
    justify-content: center;
    margin-bottom: 72px;
  }

  .loading-section {
    margin-bottom: 9px;
    text-align: center;
  }

  &__submit,
  &__close {
    min-width: 343px;
  }

  &__submit {
    margin-bottom: 16px;
  }

  // NOTE: iPhone6/7/8 の width が 375px なのでそれよりも狭い場合
  @media screen and (max-width: ($small-contents-width - 1px)) {
    &__submit,
    &__close {
      min-width: auto;
      width: 288px;
    }
  }
}
</style>
